import { ErrorCode, ReportStatus, enumKeyOf, type WorkDaySummary } from '@mai-team-amusement/core';
import { ApiError, useCommonT, useLocaleState, useSystemMe } from '@mai-team-amusement/ui';
import {
  EMPTY_RICH_TEXT,
  isEmptyRichText,
  sameRichText,
  zReportSections,
  type ReportSections,
  type ReportView,
  type RichText,
} from '@mai-work-report/contracts';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HistoryIcon from '@mui/icons-material/History';
import LockIcon from '@mui/icons-material/Lock';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useBlocker, useParams, useSearchParams } from 'react-router';
import { z } from 'zod';
import { ReportQueryKey, reportApi } from '../api.js';
import { ErrorAlert, PageHeader, QueryView, Section, Space } from '../components/common.js';
import { RevisionDrawer } from '../components/RevisionDrawer.js';
import { RichTextEditor } from '../components/RichTextEditor.js';
import { RichTextView } from '../components/RichTextView.js';
import {
  WeekStep,
  currentWeek,
  formatDateTime,
  formatDayWithWeekday,
  formatDeadline,
  formatWorkTime,
  mondayOf,
  shiftWeek,
} from '../format.js';
import { useReportT } from '../i18n/index.js';

const DRAFT_PREFIX = 'mai-report-draft';
const DRAFT_SAVE_DELAY_MS = 800;
const SAVED_NOTICE_MS = 3000;
const DATE_FIELD_WIDTH = 170;

const zDraft = z.object({
  version: z.number().int().nullable(),
  savedAt: z.string(),
  sections: zReportSections,
});

type Draft = z.output<typeof zDraft>;

const draftKey = (sub: string, weekStart: string) => `${DRAFT_PREFIX}:${sub}:${weekStart}`;

/** Browser storage may be unavailable (private mode, blocked site data): drafts are best effort. */
function readDraft(key: string): Draft | null {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw === null ? null : zDraft.safeParse(JSON.parse(raw));
    return parsed?.success === true ? parsed.data : null;
  } catch {
    return null;
  }
}

function writeDraft(key: string, draft: Draft | null) {
  try {
    if (draft === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(draft));
    }
  } catch {
    // Not available: the explicit save still works.
  }
}

const sectionsOf = (view: ReportView): ReportSections => ({
  planThisWeek: view.planThisWeek,
  days: view.days,
  planNextWeek: view.planNextWeek,
});

function sameSections(a: ReportSections, b: ReportSections): boolean {
  return (
    sameRichText(a.planThisWeek, b.planThisWeek) &&
    sameRichText(a.planNextWeek, b.planNextWeek) &&
    a.days.every((day, index) =>
      sameRichText(day.content, b.days[index]?.content ?? EMPTY_RICH_TEXT),
    )
  );
}

function WorkTime({
  summary,
  available,
}: {
  summary: WorkDaySummary | undefined;
  available: boolean;
}) {
  const t = useReportT();
  if (!available) {
    return (
      <Typography component="span" color="text.secondary">
        [—]
      </Typography>
    );
  }
  return (
    <Stack component="span" direction="row" spacing={Space.Xs} sx={{ alignItems: 'center' }}>
      <Typography component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        [{formatWorkTime(summary)}]
      </Typography>
      {summary?.isModified === true ? (
        <Chip size="small" variant="outlined" color="info" label={t('report.modified')} />
      ) : null}
    </Stack>
  );
}

function SectionBody({
  editable,
  label,
  value,
  onChange,
}: {
  editable: boolean;
  label: string;
  value: RichText;
  onChange: (value: RichText) => void;
}) {
  const t = useReportT();
  if (editable) {
    return <RichTextEditor label={label} value={value} onChange={onChange} />;
  }
  return isEmptyRichText(value) ? (
    <Typography color="text.secondary">{t('report.notWritten')}</Typography>
  ) : (
    <RichTextView value={value} />
  );
}

function ReportEditor({
  view,
  onReload,
  onSaved,
}: {
  view: ReportView;
  onReload: () => void;
  /** The editor is remounted with the saved version, so the page shows the notice. */
  onSaved: () => void;
}) {
  const t = useReportT();
  const common = useCommonT();
  const { locale } = useLocaleState();
  const queryClient = useQueryClient();
  const key = draftKey(view.user.sub, view.weekStart);
  const editable = view.permissions.edit;
  const [sections, setSections] = useState(() => sectionsOf(view));
  const [draft, setDraft] = useState<Draft | null>(() => {
    const stored = readDraft(key);
    return stored !== null &&
      stored.version === view.version &&
      editable &&
      !sameSections(stored.sections, sectionsOf(view))
      ? stored
      : null;
  });
  const [historyOpen, setHistoryOpen] = useState(false);
  const dirty = editable && !sameSections(sections, sectionsOf(view));

  // Keep unsaved text on this device until it is saved.
  useEffect(() => {
    if (!dirty) {
      return undefined;
    }
    const timer = setTimeout(() => {
      writeDraft(key, { version: view.version, savedAt: new Date().toISOString(), sections });
    }, DRAFT_SAVE_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [dirty, key, sections, view.version]);

  useEffect(() => {
    if (!dirty) {
      return undefined;
    }
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
    };
  }, [dirty]);

  const blocker = useBlocker(dirty);
  const reportKey = [...ReportQueryKey.Report, view.user.sub, view.weekStart];

  const save = useMutation({
    mutationFn: () =>
      reportApi.save(view.user.sub, view.weekStart, { version: view.version, ...sections }),
    onSuccess: (next) => {
      writeDraft(key, null);
      onSaved();
      queryClient.setQueryData(reportKey, next);
      void queryClient.invalidateQueries({ queryKey: ReportQueryKey.Revisions });
      void queryClient.invalidateQueries({ queryKey: ReportQueryKey.History });
    },
  });
  const decide = useMutation({
    mutationFn: (confirm: boolean) =>
      confirm
        ? reportApi.confirm(view.user.sub, view.weekStart)
        : reportApi.unconfirm(view.user.sub, view.weekStart),
    onSuccess: (next) => {
      queryClient.setQueryData(reportKey, next);
      void queryClient.invalidateQueries({ queryKey: ReportQueryKey.Team });
    },
  });
  const conflict = save.error instanceof ApiError && save.error.code === ErrorCode.ReportConflict;
  const now = Date.now();
  const deadline = formatDeadline(view.lockAt, view.timeZone, common);
  const setDay = (date: string, content: RichText) => {
    setSections((current) => ({
      ...current,
      days: current.days.map((day) => (day.date === date ? { date, content } : day)),
    }));
  };
  const workDays = new Map((view.workDays ?? []).map((day) => [day.workDate, day]));

  return (
    <Stack spacing={Space.Md}>
      <Stack direction="row" spacing={Space.Sm} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip
          color={view.status === ReportStatus.Confirmed ? 'success' : 'default'}
          label={common(`enum.ReportStatus.${enumKeyOf(ReportStatus, view.status)}`)}
        />
        {Date.parse(view.lockAt) > now ? (
          <Typography color="text.secondary">{t('report.editableUntil', { deadline })}</Typography>
        ) : (
          <Stack direction="row" spacing={Space.Xxs} sx={{ alignItems: 'center' }}>
            <LockIcon fontSize="small" color="action" />
            <Typography color="text.secondary">{t('report.lockedSince', { deadline })}</Typography>
          </Stack>
        )}
        {view.confirmedBy === null || view.confirmedAt === null ? null : (
          <Typography color="text.secondary">
            {t('report.confirmedBy', {
              name: view.confirmedBy.name ?? '',
              time: formatDateTime(view.confirmedAt, view.timeZone, locale),
            })}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {view.exists ? (
          <Button
            startIcon={<HistoryIcon />}
            onClick={() => {
              setHistoryOpen(true);
            }}
          >
            {t('report.history')}
          </Button>
        ) : null}
        {view.permissions.confirm ? (
          <Button
            variant="contained"
            color="success"
            loading={decide.isPending}
            onClick={() => {
              decide.mutate(true);
            }}
          >
            {common('action.confirm')}
          </Button>
        ) : null}
        {view.permissions.unconfirm ? (
          <Button
            color="warning"
            loading={decide.isPending}
            onClick={() => {
              decide.mutate(false);
            }}
          >
            {common('action.unconfirm')}
          </Button>
        ) : null}
        {editable ? (
          <Button
            variant="contained"
            disabled={!dirty}
            loading={save.isPending}
            onClick={() => {
              save.mutate();
            }}
          >
            {t('report.save')}
          </Button>
        ) : null}
      </Stack>
      {conflict ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={onReload}>
              {t('report.reload')}
            </Button>
          }
        >
          {t('report.conflict')}
        </Alert>
      ) : (
        <ErrorAlert error={save.error} />
      )}
      <ErrorAlert error={decide.error} />
      {draft === null ? null : (
        <Alert
          severity="info"
          action={
            <Stack direction="row">
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setSections(draft.sections);
                  setDraft(null);
                }}
              >
                {t('report.restoreDraft')}
              </Button>
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  writeDraft(key, null);
                  setDraft(null);
                }}
              >
                {t('report.discardDraft')}
              </Button>
            </Stack>
          }
        >
          {t('report.draftFound', { time: formatDateTime(draft.savedAt, view.timeZone, locale) })}
        </Alert>
      )}
      {view.inherited && editable ? <Alert severity="info">{t('report.inherited')}</Alert> : null}
      {view.workDays === null ? (
        <Alert severity="warning">{t('report.workTimesUnavailable')}</Alert>
      ) : null}

      <Section title={t('report.planThisWeek')}>
        <SectionBody
          editable={editable}
          label={t('report.planThisWeek')}
          value={sections.planThisWeek}
          onChange={(planThisWeek) => {
            setSections((current) => ({ ...current, planThisWeek }));
          }}
        />
      </Section>
      <Section title={t('report.days')}>
        <Stack spacing={Space.Md}>
          {sections.days.map((day) => {
            const label = formatDayWithWeekday(day.date, common);
            return (
              <Stack key={day.date} spacing={Space.Xs} component="section" aria-label={label}>
                <Stack direction="row" spacing={Space.Xs} sx={{ alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 'bold' }}>[{label}]</Typography>
                  <WorkTime summary={workDays.get(day.date)} available={view.workDays !== null} />
                </Stack>
                <SectionBody
                  editable={editable}
                  label={label}
                  value={day.content}
                  onChange={(content) => {
                    setDay(day.date, content);
                  }}
                />
              </Stack>
            );
          })}
        </Stack>
      </Section>
      <Section title={t('report.planNextWeek')}>
        <SectionBody
          editable={editable}
          label={t('report.planNextWeek')}
          value={sections.planNextWeek}
          onChange={(planNextWeek) => {
            setSections((current) => ({ ...current, planNextWeek }));
          }}
        />
      </Section>

      {historyOpen ? (
        <RevisionDrawer
          view={view}
          onClose={() => {
            setHistoryOpen(false);
          }}
        />
      ) : null}
      <Dialog open={blocker.state === 'blocked'} onClose={() => blocker.reset?.()}>
        <DialogTitle>{t('report.leaveTitle')}</DialogTitle>
        <DialogActions>
          <Button onClick={() => blocker.reset?.()}>{t('report.stay')}</Button>
          <Button color="error" onClick={() => blocker.proceed?.()}>
            {t('report.leave')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/** A weekly report: the signed-in user's (`/`) or a supervised member's (`/reports/:sub`). */
export function ReportPage() {
  const t = useReportT();
  const me = useSystemMe();
  const { sub = me.user.sub } = useParams();
  const [params, setParams] = useSearchParams();
  const thisWeek = currentWeek(me.user.timeZone);
  const requested = params.get('week');
  const weekStart = requested === null ? thisWeek : mondayOf(requested);
  const report = useQuery({
    queryKey: [...ReportQueryKey.Report, sub, weekStart],
    queryFn: () => reportApi.report(sub, weekStart),
  });
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!saved) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setSaved(false);
    }, SAVED_NOTICE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [saved]);
  const goTo = (week: string) => {
    const next = new URLSearchParams(params);
    next.set('week', week);
    setParams(next);
  };
  const own = sub === me.user.sub;
  const title =
    own || report.data === undefined
      ? t('report.title')
      : t('report.titleOf', {
          name: `${report.data.user.familyName} ${report.data.user.givenName}`,
        });

  return (
    <>
      <PageHeader
        title={title}
        actions={
          <>
            <IconButton
              aria-label={t('report.previousWeek')}
              onClick={() => {
                goTo(shiftWeek(weekStart, WeekStep.Previous));
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <TextField
              type="date"
              size="small"
              label={t('report.pickWeek')}
              value={weekStart}
              slotProps={{ htmlInput: { max: thisWeek }, inputLabel: { shrink: true } }}
              sx={{ width: DATE_FIELD_WIDTH }}
              onChange={(event) => {
                if (event.target.value !== '') {
                  goTo(mondayOf(event.target.value));
                }
              }}
            />
            <IconButton
              aria-label={t('report.nextWeek')}
              disabled={weekStart >= thisWeek}
              onClick={() => {
                goTo(shiftWeek(weekStart, WeekStep.Next));
              }}
            >
              <ChevronRightIcon />
            </IconButton>
            {weekStart === thisWeek ? null : (
              <Button
                onClick={() => {
                  goTo(thisWeek);
                }}
              >
                {t('report.thisWeek')}
              </Button>
            )}
          </>
        }
      />
      {saved ? (
        <Alert severity="success" sx={{ mb: Space.Md }}>
          {t('report.saved')}
        </Alert>
      ) : null}
      <QueryView query={report}>
        {(view) => (
          <ReportEditor
            key={`${view.user.sub}:${view.weekStart}:${String(view.version)}:${String(view.status)}`}
            view={view}
            onReload={() => {
              void report.refetch();
            }}
            onSaved={() => {
              setSaved(true);
            }}
          />
        )}
      </QueryView>
    </>
  );
}
